import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  type EmailMessagesFilterInput,
  EmailMessageTimePeriodFilter,
  useContactQuery,
} from '@dashboard/generated/wp/platform';
import {
  castEmailMessageEnvironmentFilter,
  castEmailMessageEventTypeFilter,
  castEmailMessageTimePeriodFilter,
  DEFAULT_EMAIL_MESSAGE_ENVIRONMENT_FILTER,
  DEFAULT_EMAIL_MESSAGE_EVENT_TYPE_FILTER,
} from '@dashboard/graphql/platform/cast-value';

export function useCurrentContactId() {
  return useParams<{ id: string }>().id!;
}

export function useCurrentContactQuery() {
  const id = useCurrentContactId();
  return useContactQuery({ variables: { id } });
}

type TStatus = readonly [EmailMessagesFilterInput, (val: EmailMessagesFilterInput) => void];

const DEFAULT_EMAIL_MESSAGE_TIME_PERIOD_FILTER = EmailMessageTimePeriodFilter.All;

export function useContactEmailMessagesFilters(): TStatus {
  const recipientContactId = useCurrentContactId();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = useMemo<EmailMessagesFilterInput>(
    () => ({
      recipientContactId,
      environment: castEmailMessageEnvironmentFilter(searchParams.get('environment')),
      timePeriod: castEmailMessageTimePeriodFilter(
        searchParams.get('timePeriod'),
        DEFAULT_EMAIL_MESSAGE_TIME_PERIOD_FILTER
      ),
      eventType: castEmailMessageEventTypeFilter(searchParams.get('eventType')),
      sourceEmailMessagesBatchId: searchParams.get('batchId') ?? null,
      query: searchParams.get('query') ?? null,
    }),
    [recipientContactId, searchParams]
  );

  const setFilter = useCallback(
    (filter: EmailMessagesFilterInput) => {
      searchParams.delete('page');

      if (filter.environment === DEFAULT_EMAIL_MESSAGE_ENVIRONMENT_FILTER) {
        searchParams.delete('environment');
      } else if (filter.environment) {
        searchParams.set('environment', filter.environment);
      }

      if (filter.timePeriod === DEFAULT_EMAIL_MESSAGE_TIME_PERIOD_FILTER) {
        searchParams.delete('timePeriod');
      } else if (filter.timePeriod) {
        searchParams.set('timePeriod', filter.timePeriod);
      }

      if (filter.eventType === DEFAULT_EMAIL_MESSAGE_EVENT_TYPE_FILTER) {
        searchParams.delete('eventType');
      } else if (filter.eventType) {
        searchParams.set('eventType', filter.eventType);
      }

      if (filter.query === null) {
        searchParams.delete('query');
      } else if (typeof filter.query === 'string') {
        if (filter.query.trim().length === 0) {
          searchParams.delete('query');
        } else {
          searchParams.set('query', filter.query);
        }
      }

      if (!filter.sourceEmailMessagesBatchId) {
        searchParams.delete('batchId');
      } else {
        searchParams.set('batchId', filter.sourceEmailMessagesBatchId);
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  return useMemo(() => [filter, setFilter], [filter, setFilter]);
}
